/**
 * Structured floor-level diff between two `EC2_TRIAL_STAGE_3.0` JSON strings,
 * for the `/trial` editor's confirm-before-save review. Deliberately not a
 * text line-diff — floors are the unit a developer thinks in, and JSON
 * re-serialization (key order, whitespace) would otherwise show noise that
 * isn't a real change.
 */

interface TrialStageLike {
  floor?: number;
  [key: string]: unknown;
}
interface TrialDataLike {
  leaderboardVersion?: number;
  stages?: TrialStageLike[];
}

export interface FloorDiffEntry {
  floor: number;
  kind: "added" | "removed" | "changed";
  before?: string;
  after?: string;
}

export interface TrialDiff {
  /** True if either side failed to parse — floor diff is unavailable then. */
  unparseable: boolean;
  leaderboardVersionBefore?: number;
  leaderboardVersionAfter?: number;
  leaderboardVersionChanged: boolean;
  floors: FloorDiffEntry[];
}

function tryParse(raw: string): TrialDataLike | undefined {
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as TrialDataLike) : undefined;
  } catch {
    return undefined;
  }
}

export function diffTrialData(beforeRaw: string, afterRaw: string): TrialDiff {
  const before = tryParse(beforeRaw);
  const after = tryParse(afterRaw);

  if (!before || !after) {
    return { unparseable: true, leaderboardVersionChanged: false, floors: [] };
  }

  const beforeByFloor = new Map<number, TrialStageLike>();
  for (const s of before.stages ?? []) {
    if (typeof s.floor === "number") beforeByFloor.set(s.floor, s);
  }
  const afterByFloor = new Map<number, TrialStageLike>();
  for (const s of after.stages ?? []) {
    if (typeof s.floor === "number") afterByFloor.set(s.floor, s);
  }

  const floors: FloorDiffEntry[] = [];
  const allFloors = new Set([...beforeByFloor.keys(), ...afterByFloor.keys()]);
  for (const floor of [...allFloors].sort((a, b) => a - b)) {
    const b = beforeByFloor.get(floor);
    const a = afterByFloor.get(floor);
    if (b && !a) {
      floors.push({ floor, kind: "removed", before: JSON.stringify(b, null, 2) });
    } else if (!b && a) {
      floors.push({ floor, kind: "added", after: JSON.stringify(a, null, 2) });
    } else if (b && a) {
      const bs = JSON.stringify(b);
      const as = JSON.stringify(a);
      if (bs !== as) {
        floors.push({
          floor,
          kind: "changed",
          before: JSON.stringify(b, null, 2),
          after: JSON.stringify(a, null, 2),
        });
      }
    }
  }

  return {
    unparseable: false,
    leaderboardVersionBefore: before.leaderboardVersion,
    leaderboardVersionAfter: after.leaderboardVersion,
    leaderboardVersionChanged: before.leaderboardVersion !== after.leaderboardVersion,
    floors,
  };
}
